import BugReportIcon from '@mui/icons-material/BugReport';
import CloseIcon from '@mui/icons-material/Close';
import { Badge, Box, CircularProgress, Drawer, IconButton, Typography } from '@mui/material';
import html2canvas from 'html2canvas';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import NewTicketForm from '@/Modules/HelpdeskModule/shared/components/new-ticket-form/new-ticket-form.component';
import { useCreateTicketMutation } from '@/Modules/HelpdeskModule/shared/hooks/useHelpdeskQueries';
import { CreateTicketPayload } from '@/Modules/HelpdeskModule/shared/types';
import { useLocalization } from '@/shared/hooks/useLocalization';
import ReduxStore from '@/shared/redux/store';
import { getSessionCorrelationId } from '@/shared/services/helpers/auth-helper/auth-helper.service';
import { useBugReportStore } from '@/shared/stores/bug-report.store';
import { buildDedupKey, buildPrefill, formatMinimalContext } from '@/shared/utils/bug-report.utils';

import { BugReportPrefill } from './BugReporter.types';

const BugReporterWidget: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { isRTLDirect: isRTL } = useLocalization();
  const { hasCapture, flush, setSubmittedKey, discardCapture } = useBugReportStore();

  // Discard captures from a different page when the user navigates away
  useEffect(() => {
    const { capturedContext } = useBugReportStore.getState();
    if (capturedContext && capturedContext.route !== location.pathname) {
      discardCapture();
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [screenshotFailed, setScreenshotFailed] = useState(false);
  const [prefill, setPrefill] = useState<BugReportPrefill | null>(null);
  const [dedupKey, setDedupKey] = useState<string | null>(null);
  const createMutation = useCreateTicketMutation();

  const captureScreenshot = async (): Promise<File | undefined> => {
    try {
      const canvas = await html2canvas(document.body, {
        logging: false,
        useCORS: true,
        allowTaint: true,
        scale: 0.5
      });
      const blob = await new Promise<Blob>((resolve, reject) =>
        canvas.toBlob((b) => (b ? resolve(b) : reject()), 'image/png')
      );
      return new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' });
    } catch {
      return undefined;
    }
  };

  const handleClick = async (): Promise<void> => {
    setCapturing(true);
    try {
      const screenshotFile = await captureScreenshot();
      setScreenshotFailed(!screenshotFile);
      const capturedContext = flush();

      if (capturedContext) {
        const key = buildDedupKey(
          capturedContext.failedCall.method,
          capturedContext.failedCall.endpoint,
          capturedContext.failedCall.status
        );
        setDedupKey(key);
        setPrefill({
          ...buildPrefill(capturedContext),
          screenshotFile
        });
      } else {
        const sessionId = getSessionCorrelationId();
        const userId = ReduxStore.getState().auth.userId ?? '';
        setDedupKey(null);
        setPrefill({
          title: '',
          stepsToReproduce: formatMinimalContext({
            route: window.location.pathname,
            sessionId,
            userId
          }),
          screenshotFile
        });
      }
      setDrawerOpen(true);
    } finally {
      setCapturing(false);
    }
  };

  const handleSubmit = (payload: CreateTicketPayload): void => {
    createMutation.mutate(payload, {
      onSuccess: () => {
        if (dedupKey) setSubmittedKey(dedupKey);
        setDrawerOpen(false);
        setPrefill(null);
        setDedupKey(null);
      }
    });
  };

  const handleClose = (): void => {
    setDrawerOpen(false);
    setPrefill(null);
    setDedupKey(null);
    setScreenshotFailed(false);
  };

  const hasPrefill = prefill?.title || prefill?.stepsToReproduce;

  return (
    <>
      <Badge
        color="error"
        variant="dot"
        invisible={!hasCapture}
        sx={{ '& .MuiBadge-dot': { width: 10, height: 10, borderRadius: '50%' } }}
      >
        <IconButton
          onClick={handleClick}
          disabled={capturing}
          aria-label="raise-ticket"
          sx={{
            color: '#0274A1',
            '&:hover': { bgcolor: 'rgba(2, 116, 161, 0.08)' },
            '&.Mui-disabled': { color: '#5ba8c4' }
          }}
        >
          {capturing ? (
            <CircularProgress size={22} color="inherit" />
          ) : (
            <BugReportIcon sx={{ fontSize: 22 }} />
          )}
        </IconButton>
      </Badge>

      <Drawer
        anchor={isRTL ? 'left' : 'right'}
        open={drawerOpen}
        onClose={handleClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: '60%' }, overflow: 'auto' } }}
      >
        <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#F2F4F7', minHeight: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              mb: 2.5
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '20px', color: '#001048' }}>
                {hasPrefill ? t('bugReporter.titlePrefilled') : t('bugReporter.titleManual')}
              </Typography>
              <Typography sx={{ fontSize: '13px', color: '#697599', mt: 0.25 }}>
                {hasPrefill ? t('bugReporter.subtitlePrefilled') : t('bugReporter.subtitleManual')}
              </Typography>
              {screenshotFailed && (
                <Typography sx={{ fontSize: '12px', color: '#D97706', mt: 0.5 }}>
                  {t('bugReporter.screenshotFailed')}
                </Typography>
              )}
            </Box>
            <IconButton onClick={handleClose} size="small" sx={{ mt: 0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {prefill !== null && (
            <NewTicketForm
              prefill={prefill}
              onSubmit={handleSubmit}
              onCancel={handleClose}
              isSubmitting={createMutation.isPending}
            />
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default BugReporterWidget;
